import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { apiGet } from '../../lib/api';
import { toast } from 'sonner';
import { Printer, FileText, AlertTriangle, Package, Users, Wallet, GitBranch } from 'lucide-react';

const fmtINR = (n) => {
  const v = Number(n) || 0;
  if (Math.abs(v) >= 1e7) return `₹${(v / 1e7).toFixed(2)} Cr`;
  if (Math.abs(v) >= 1e5) return `₹${(v / 1e5).toFixed(2)} L`;
  return `₹${Math.round(v).toLocaleString('en-IN')}`;
};

const Card = ({ icon: Icon, title, children, testid }) => (
  <section data-testid={testid} className="rounded-lg border bg-white p-4 shadow-sm break-inside-avoid print:shadow-none print:border-slate-300">
    <h3 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-slate-700 mb-3">
      <Icon size={16} className="text-indigo-600" /> {title}
    </h3>
    {children}
  </section>
);

const Row = ({ label, value, accent }) => (
  <div className="flex items-baseline justify-between py-1 border-b border-slate-100 last:border-0">
    <span className="text-xs text-slate-600">{label}</span>
    <span className={`text-sm font-semibold tabular-nums ${accent || 'text-slate-900'}`}>{value}</span>
  </div>
);

export default function ProjectDeepDive() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await apiGet(`/mis/project/${id}/deep-dive`);
        setData(r);
      } catch (e) {
        toast.error('Could not load deep-dive');
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  if (loading) return <div className="p-6 text-slate-500">Loading deep-dive…</div>;
  if (!data) return <div className="p-6 text-rose-600">No data.</div>;

  const f = data.financials || {};
  const s = data.schedule || {};
  const issues = data.open_issues || {};

  return (
    <div className="p-6 max-w-6xl mx-auto print:p-0" data-testid="project-deep-dive-page">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body, .print-area { background: white !important; }
        }
      `}</style>
      <div className="flex items-center justify-between mb-4 no-print">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-display">{data.project.name}</h1>
          <p className="text-xs text-slate-500 mt-0.5">
            {data.project.client || '—'} · Status: {data.project.status || '—'} ·
            {data.project.start_date || '—'} → {data.project.end_date || '—'} ·
            Progress: {data.project.progress}%
          </p>
        </div>
        <button
          onClick={() => window.print()}
          data-testid="deep-dive-print-btn"
          className="inline-flex items-center gap-1.5 rounded border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          <Printer size={14} /> Export PDF
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 print-area">
        <Card icon={Wallet} title="Financials" testid="dd-financials">
          <Row label="Contract Value" value={fmtINR(f.contract_value)} />
          <Row label="Certified to date" value={fmtINR(f.certified)} />
          <Row label="Billed" value={fmtINR(f.billed)} />
          <Row label="Collected" value={fmtINR(f.collected)} accent="text-emerald-700" />
          <Row label="Committed Cost" value={fmtINR(f.committed)} />
          <Row label="Actual Cost (paid)" value={fmtINR(f.actual_cost)} />
          <Row
            label="Margin Estimate"
            value={`${f.margin_estimate_pct}%`}
            accent={f.margin_estimate_pct < 5 ? 'text-rose-700' : 'text-emerald-700'}
          />
        </Card>

        <Card icon={GitBranch} title="Schedule (WBS)" testid="dd-schedule">
          <Row label="WBS items total" value={s.wbs_total} />
          <Row label="WBS items completed" value={s.wbs_done} />
          <Row label="WBS progress" value={`${s.wbs_progress_pct}%`} />
          <div className="mt-3 h-2 w-full bg-slate-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-indigo-600 transition-all"
              style={{ width: `${Math.min(100, s.wbs_progress_pct || 0)}%` }}
            />
          </div>
        </Card>

        <Card icon={Users} title="Labour Cost Trend (last 6 months)" testid="dd-labour">
          {data.labour_trend?.length ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-slate-500 border-b">
                  <th className="text-left py-1">Month</th>
                  <th className="text-right py-1">Headcount</th>
                  <th className="text-right py-1">Cost</th>
                </tr>
              </thead>
              <tbody>
                {data.labour_trend.map((r) => (
                  <tr key={r.month} className="border-b border-slate-100 last:border-0">
                    <td className="py-1">{r.month}</td>
                    <td className="py-1 text-right tabular-nums">{r.headcount}</td>
                    <td className="py-1 text-right tabular-nums">{fmtINR(r.cost)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-xs text-slate-500 italic">No labour-attendance data yet.</p>
          )}
        </Card>

        <Card icon={Package} title="Top 10 Materials by Value" testid="dd-materials">
          {data.material_top10?.length ? (
            <ol className="space-y-1 text-sm">
              {data.material_top10.map((m, i) => (
                <li key={i} className="flex items-baseline justify-between border-b border-slate-100 last:border-0 py-1">
                  <span className="text-slate-700 truncate pr-2">{i + 1}. {m.material}</span>
                  <span className="font-semibold tabular-nums">{fmtINR(m.value)}</span>
                </li>
              ))}
            </ol>
          ) : (
            <p className="text-xs text-slate-500 italic">No GRN data yet.</p>
          )}
        </Card>

        <Card icon={AlertTriangle} title="Open Issues" testid="dd-issues">
          <Row label="Hindrances Open" value={issues.hindrances_open} accent={issues.hindrances_open > 0 ? 'text-amber-700' : ''} />
          <Row label="NCRs Open" value={issues.ncrs_open} accent={issues.ncrs_open > 0 ? 'text-rose-700' : ''} />
          <Row label="Approvals Pending" value={issues.approvals_pending} />
        </Card>

        <Card icon={FileText} title="Summary" testid="dd-summary">
          <p className="text-sm text-slate-700">
            <b>{data.project.name}</b> for {data.project.client || 'client'} is at
            <b> {data.project.progress}% </b> progress with an estimated margin of
            <b> {f.margin_estimate_pct}%</b>. {f.contract_value > 0 ? `Contract value ${fmtINR(f.contract_value)} ` : ''}
            {f.committed > 0 ? `with ${fmtINR(f.committed)} already committed.` : ''}
          </p>
        </Card>
      </div>
    </div>
  );
}
